"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  name: "System audit",
  description: "We begin by mapping your existing operational bottlenecks. Our architects analyze your current data flow to identify where manual friction slows down your growth and where automation can reclaim your time.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_audit_blueprint.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_audit_blueprint.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_audit_blueprint.webp-wvc-sizes",
  alt: "Architectural blueprint of a backend system workflow"
}, {
  number: "02",
  name: "CRM pipeline design",
  description: "We build custom communication pipelines that trigger automatically based on project status. From initial lead capture to final invoice follow-up, your client interactions remain professional and consistent.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/crm_pipeline_design.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/crm_pipeline_design.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/crm_pipeline_design.png-wvc-sizes",
  alt: "Digital interface showing automated CRM communication flows"
}, {
  number: "03",
  name: "FSM infrastructure",
  description: "We configure your Field Service Management tools to track labor, materials, and site progress in real-time. This creates a single source of truth for your team, eliminating guesswork and scheduling conflicts.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/fsm_infrastructure.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/fsm_infrastructure.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/fsm_infrastructure.png-wvc-sizes",
  alt: "Dashboard view of field service operations and scheduling"
}, {
  number: "04",
  name: "Automated bookkeeping",
  description: "We bridge the gap between your field operations and your financial records. Every completed job automatically generates accurate invoices and expense logs, ensuring your books are always audit-ready.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/automated_bookkeeping.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/automated_bookkeeping.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/automated_bookkeeping.webp-wvc-sizes",
  alt: "Automated financial reporting and invoicing software interface"
}, {
  number: "05",
  name: "System deployment",
  description: "We hand over a fully integrated, modernized backend. Our team provides the documentation and training needed to ensure your staff can operate the new infrastructure with confidence from day one.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_deployment.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_deployment.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713186/img/system_deployment.webp-wvc-sizes",
  alt: "Consultant finalizing a system deployment for a contractor"
}];
export default function Integration() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="10501" id="the-integration-process" className="bg-background text-foreground py-28 md:py-36">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-6 lg:px-12">
        {}
        <div ref={headingRef} className="relative overflow-hidden" aria-hidden="true">
          <div className={`h-px w-full bg-foreground origin-left transition-transform duration-[520ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${headingVisible ? "scale-x-100" : "scale-x-0"}`} />
        </div>

        {}
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3 md:gap-4 lg:gap-6 mt-6">
          <h2 className={`col-span-4 md:col-span-6 lg:col-span-7 font-default font-medium text-foreground transition-all duration-[420ms] delay-[120ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          fontSize: "clamp(2rem, 4vw, 3.5rem)",
          letterSpacing: "-0.025em",
          lineHeight: "1.0"
        }}>
            The Integration Process
          </h2>
          <p className={`col-span-4 md:col-span-6 lg:col-span-5 lg:col-start-8 self-end font-default text-muted-foreground transition-all duration-[420ms] delay-[200ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
          letterSpacing: "-0.005em",
          lineHeight: "1.55"
        }}>
            We transform fragmented operations into a cohesive digital architecture. 
            Our five-stage integration framework ensures your business runs with 
            the precision of a modern enterprise, built specifically for the trades.
          </p>
        </div>

        {}
        <ol className="mt-16 md:mt-20">
          {steps.map((step, i) => <StepRow key={step.number} step={step} index={i} />)}
        </ol>
      </div>
    </section>;
}
type StepData = {
  number: string;
  name: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function StepRow({
  step,
  index
}: {
  step: StepData;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  const delay = 60 * index;
  return <li data-index={index} ref={ref} className="group block border-t border-foreground/10">
      <div className={`grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3 md:gap-4 lg:gap-6 py-8 md:py-14 transition-all duration-[360ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
      transitionDelay: `${delay}ms`
    }}>
        {}
        <div className="col-span-4 md:col-span-6 lg:col-span-2 flex items-baseline lg:block">
          <span className="font-default font-medium text-primary" style={{
          fontSize: "clamp(2rem, 4vw, 3.5rem)",
          letterSpacing: "-0.025em",
          lineHeight: "1.0"
        }}>
            {step.number}
          </span>
        </div>

        <div className="col-span-4 md:col-span-6 lg:col-span-5 lg:col-start-3 -mt-2 lg:mt-0">
          <h3 className="font-default font-medium text-foreground" style={{
          fontSize: "clamp(1.25rem, 1.6vw, 1.625rem)",
          letterSpacing: "-0.012em",
          lineHeight: "1.15"
        }}>
            {step.name}
          </h3>
          <p className="mt-4 font-default text-muted-foreground" style={{
          fontSize: "0.9375rem",
          letterSpacing: "0",
          lineHeight: "1.55"
        }}>
            {step.description}
          </p>
        </div>

        {}
        <div className="col-span-4 md:col-span-6 lg:col-span-5 lg:col-start-8 mt-4 lg:mt-0">
          <div className="relative w-full overflow-hidden bg-muted" style={{
          aspectRatio: "4 / 5"
        }}>
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[280ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] group-hover:scale-[1.02]" />
          </div>
        </div>
      </div>
    </li>;
}